// UsersPage.tsx — Section VI: Users.
//
// "Profiles, friend codes, and the most active tables on the platform."
// (Lore-approved US-03 revised: "most active tables")
//
// Wire: auth_canister.admin_get_metrics() via admin_get_aggregated_metrics().
// Empty state: "No players yet. The first to claim a designation arrives soon."

import React from 'react';
import type { Identity } from '@dfinity/agent';
import MetricCard from '../components/MetricCard';
import { useAdminMetrics } from '../hooks/useAdminMetrics';

interface UsersPageProps {
  identity: Identity | null;
}

function formatNat(n: bigint): string {
  return Number(n).toLocaleString('en-US');
}

export default function UsersPage({ identity }: UsersPageProps): React.ReactElement {
  const { metrics, isLoading, lastFetchedAt, refresh } = useAdminMetrics(identity);

  const auth = metrics?.auth?.[0] ?? null;
  const hasData = auth !== null;

  return (
    <section className="section" id="users" aria-labelledby="users-title">
      <header className="section-header">
        <span className="section-eyebrow">Section VI</span>
        <h2 className="section-title" id="users-title">Users</h2>
        <p className="section-lede">
          Profiles, friend codes, and the most active tables on the platform.
        </p>
        {lastFetchedAt && (
          <p className="section-meta">
            Last refreshed · {lastFetchedAt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })} UTC
            {' '}
            <button className="btn-inline" onClick={refresh} disabled={isLoading}>
              {isLoading ? 'Refreshing' : 'Refresh'}
            </button>
          </p>
        )}
      </header>

      {!hasData && !isLoading ? (
        <div className="empty-state">
          <p className="empty-state-text">
            No players yet. The first to claim a designation arrives soon.
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-4">
            <MetricCard
              label="Total Profiles"
              numeral={auth ? formatNat(auth.totalProfiles) : '—'}
              caption="Lifetime accounts created."
              trend={auth ? `+${auth.profilesCreatedLast24h} / 24h` : undefined}
              trendDir="up"
            />
            <MetricCard
              label="New Today"
              numeral={auth ? formatNat(auth.profilesCreatedLast24h) : '—'}
              caption="Welcome screen completed."
            />
            <MetricCard
              label="New / 7d"
              numeral={auth ? formatNat(auth.profilesCreatedLast7d) : '—'}
              caption="Past seven days."
            />
            <MetricCard
              label="Friend Codes Issued"
              numeral={auth ? formatNat(auth.friendCodesClaimed + auth.friendCodesUnclaimed) : '—'}
              caption="Personal codes for room invites."
            />
          </div>

          <div className="panel" style={{ marginTop: 'var(--s-lg)' }}>
            <p className="panel-title">Most Active Members · 7 days</p>
            <p className="panel-subtitle">
              Ranked by completed matches. Identifiers shown as truncated principals.
            </p>
            <p className="card-caption" style={{ marginTop: 'var(--s-md)' }}>
              Active member ranking requires a dedicated leaderboard query on the game canister.
              Lifetime tracking begins post-launch.
            </p>
          </div>
        </>
      )}
    </section>
  );
}
